import type { MarkOptional } from 'ts-essentials';
import { type SelectedDataType } from '~/_shared/components/customizeMetrics/selectedDataMetric.type';
import { notEmpty } from '~/_shared/utils/array/array.helpers';
import type { HexColor } from '~/_shared/utils/colors/colors.types';
import { type SpreadsheetColumnServerModel } from '../../_shared/types/spreadsheetData/spreadsheetColumn';
import {
  apiDelete, apiPost, apiPut,
} from '../../_shared/utils/api/api.helpers';
import { type MetricsDataAction } from '../../_shared/utils/metric/metrics.types';
import { isTextEmpty } from '../../_shared/utils/text/text.helpers';
import { type BoundaryEtagServerModal } from '../../boundary/etag/etag.types';
import { type BoundaryFillType } from '../../boundary/settings/boundaryFill.type';
import { type BoundaryGroupDemographicServerModel } from '../boundaryGroups/boundaryGroups.repository';
import type { ChartMetricType } from '../mapSettings/data/charts/mapSettingsCharts.types';
import {
  type BoundaryTerritoryType, type CalculateBucketFunction,
} from './boundaryTerritoryGroup.type';
import { createTerritoryBoundaryGroupFromResponse } from './boundaryTerritoryGroups.factory';
import {
  type BoundaryStyle,
  type BoundaryTerritory,
  type BoundaryTerritoryGroup,
  type BoundaryTerritoryGroupConnectedEntityData,
} from './boundaryTerritoryGroups.state';

export type CustomBoundaryTerritoryResponse = Readonly<{
  boundary_territory_id: string;
  display_name: string;
  bucket: string;
  translate: boolean;
  custom: true;
  style: Readonly<{
    color: HexColor;
    opacity: number;
  }>;
}>;

export type GeneratedBoundaryTerritoryResponse = Readonly<{
  boundary_territory_id: string;
  display_name: string;
  translate: boolean;
  bucket: string;
  custom: false;
  style: Readonly<{
    color: HexColor;
    opacity: number;
  }>;
  is_decimal: boolean;
}>;

export type BoundaryTerritoryRequest = Readonly<{
  display_name: string;
  boundary_territory_id: string;
  translate: boolean;
  bucket?: string;
  style: Readonly<{
    color: HexColor;
    opacity: number;
  }>;
  custom: boolean;
  is_decimal?: boolean;
}>;

export type BoundaryTerritoryResponse = CustomBoundaryTerritoryResponse | GeneratedBoundaryTerritoryResponse;

type BoundaryTerritoryStyleGradientColors = Readonly<{
  minimal: {
    readonly color: HexColor;
  };
  low: {
    readonly color: HexColor;
  };
  medium: {
    readonly color: HexColor;
  };
  high: {
    readonly color: HexColor;
  };
}>;

export type BoundaryTerritoryGroupFilterDefinition = Readonly<{
  boundary_group_id: number;
  boundary_territory_group_id?: number;
  matchings: BoundaryTerritoryGroupMatchingsRequest;
  settings: BoundaryTerritoryGroupSettingsRequest;
}>;

export type BoundaryTerritoryStyleResponse = Readonly<{
  line_width: number;
  line_color: HexColor;
  gradient_colors: MarkOptional<BoundaryTerritoryStyleGradientColors, 'minimal'>;
}>;

export type BoundaryTerritoryStyleRequest = Readonly<{
  line_width: number;
  line_color: HexColor;
  gradient_colors: BoundaryTerritoryStyleGradientColors;
}>;

type SpreadsheetColumnMetricServerModel = Readonly<{
  type: SelectedDataType.SpreadsheetColumn;
  spreadsheetColumn: SpreadsheetColumnServerModel;
  dataAction: MetricsDataAction;
}>;

type DemographicMetricServerModel = Readonly<{
  type: SelectedDataType.Demographic;
  demographic: BoundaryGroupDemographicServerModel;
}>;

export type MetricServerModel = SpreadsheetColumnMetricServerModel | DemographicMetricServerModel;

export type BoundaryTerritoryLabelStyleServerModel = Readonly<{
  font_color: string;
  background_color: string;
}>;

export type BoundaryTerritoryLabelValueConfigServerModel = Readonly<{
  show_value: boolean;
  include_legend: boolean; // Toggles whether we show the column name or stat name.
  is_value_custom: boolean;
  label_value_type: BoundaryTerritoryType | null;
  label_value_column_id: string | null;
  label_value_aggregation_function: CalculateBucketFunction | null;
  demographic_id: string | null;
}>;

type BoundaryTerritoryGroupSettingsModel = Readonly<{
  allow_decimal_ranges: boolean;
  boundary_territory_type: BoundaryTerritoryType;
  bucket_column_id?: string | null;
  bucket_type?: BoundaryBucketType | null;
  bucket_distribution?: BucketDistribution;
  demographic_id?: string | null;
  metrics?: ReadonlyArray<MetricServerModel>;
  ranges_count?: number;
  boundary_style?: ReadonlyArray<{ boundaryId: number; style: BoundaryStyle }>;
  calculate_bucket_function: CalculateBucketFunction | null;
  fade_when_inactive?: boolean;
  disable_map_interaction?: boolean;
  hide_labels?: boolean;
  hide_without_data?: boolean;
  ignore_filters?: boolean;
  selection_edit_preferred_boundary_group?: { id: number };
  default_statistics?: ReadonlyArray<BoundaryTerritoryGroupDefaultStatisticServerModel>;
  boundary_territory_label_style?: BoundaryTerritoryLabelStyleServerModel;
  boundary_territory_label_config?: BoundaryTerritoryLabelValueConfigServerModel;
}>;

export type BoundaryTerritoryGroupDefaultStatisticServerModel = {
  type: ChartMetricType.SpreadsheetColumn;
  metric: string;
  virtual_spreadsheet_id: number;
} | {
  type: ChartMetricType.Demographic;
  metric: number;
};

export type BoundaryTerritoryGroupSettingsResponse = BoundaryTerritoryGroupSettingsModel & {
  boundaries_as_territories: boolean;
  boundary_fill_type?: BoundaryFillType;
  boundary_territories: readonly BoundaryTerritoryResponse[];
  calculate_bucket_function: string;
  connected_entity_data?: BoundaryTerritoryGroupConnectedEntityData;
  demographic_id: string | null;
  display_name: string | null;
  empty_is_zero: null;
  style: BoundaryTerritoryStyleResponse;
};

export type BoundaryTerritoryGroupSettingsRequest = Readonly<Partial<BoundaryTerritoryGroupSettingsModel> & {
  boundaries_as_territories?: boolean;
  boundary_fill_type: BoundaryFillType;
  boundary_territories?: readonly BoundaryTerritoryRequest[];
  boundary_territory_type: BoundaryTerritoryType;
  connected_entity_data?: BoundaryTerritoryGroupConnectedEntityData;
  style?: BoundaryTerritoryStyleRequest;
}>;

export enum BoundaryMatchingType {
  Geometry = 'geometry',
  Equal = 'equal',
  Multiequal = 'multiequal',
  Zips = 'zips'
}

export type BoundaryTerritoryGroupMatchColumnResponse = {
  column_id: string;
  original_column_id?: string;
  category: BoundaryMatchingCategory;
};

export type BoundaryTerritoryGroupMatchingsResponse = {
  matching_type: BoundaryMatchingType;
  match_columns: BoundaryTerritoryGroupMatchColumnResponse[];
};

export type BoundaryTerritoryGroupCreateRequest = {
  boundary_group_id: number;
  map_id: number;
  matchings?: BoundaryTerritoryGroupMatchingsRequest;
  settings: BoundaryTerritoryGroupSettingsRequest;
};

export type BoundaryTerritoryGroupCloneRequest = {
  boundary_territory_group_id: number;
  new_boundary_territory_group_name: string;
  map_id: number;
};

export type BoundaryTerritoryGroupConvertRequest = BoundaryTerritoryGroupCreateRequest & {
  boundary_territory_group_id?: number;
};

export type BoundaryTerritoryGroupModelResponse = {
  boundary_territory_group_id?: number;
  archived_at: string | null;
  boundary_group_id: number;
  map_id: number;
  matchings: BoundaryTerritoryGroupMatchingsResponse;
  settings: BoundaryTerritoryGroupSettingsResponse;
  etag?: BoundaryEtagServerModal;
};

export type BoundaryTerritoryGroupResponse = BoundaryTerritoryGroupModelResponse & {
  boundary_territory_group_id: number;
};

export type BoundaryTerritoryGroupsResponse = {
  boundary_territory_groups: BoundaryTerritoryGroupResponse[];
};

type BoundaryTerritoryGroupQueryRequest = {
  map_id: number;
  include_archived?: boolean;
};

export type BoundaryMatchingCategory = 'city' | 'state' | 'zip' | 'country' | 'county' | 'congressional_district' | 'census_tract' | 'block_group';

export type BoundaryBucketType = 'value' | 'value-range' | 'percentage-range';

export type BucketDistribution = 'equal-range' | 'equal-count';

export type BoundaryTerritoryGroupMatchingsRequest = {
  matching_type: BoundaryMatchingType;
  match_columns?: BoundaryTerritoryGroupMatchColumnResponse[];
};

export type BoundaryTerritoryGroupUpdateRequest = BoundaryTerritoryGroupCreateRequest & {
  boundary_territory_group_id: number;
  etag?: BoundaryEtagServerModal;
};

export const requestTerritoriesRefresh = (
  boundaryTerritories: ReadonlyArray<BoundaryTerritory>,
  originalBoundaryTerritoryGroupTerritories: ReadonlyArray<BoundaryTerritory>,
): ReadonlyArray<BoundaryTerritory> => {
  return boundaryTerritories
    .filter(territory => territory.custom)
    .map(bt => {
      let displayName = bt.displayName;
      if (isTextEmpty(displayName)) {
        displayName = originalBoundaryTerritoryGroupTerritories.find(
          item => item.boundaryTerritoryId === bt.boundaryTerritoryId
        )?.displayName ?? displayName;
      }

      return {
        ...bt,
        displayName,
      };
    });
};

export const getBoundaryTerritoryGroups = (
  clientId: number,
  request: BoundaryTerritoryGroupQueryRequest
): Promise<BoundaryTerritoryGroupsResponse> => {
  const requestUrl = `/api/clients/${clientId}/boundary-territory-group/query`;

  return apiPost(requestUrl, {
    query: request,
  });
};

export const createBoundaryTerritoryGroup = (
  clientId: number,
  request: BoundaryTerritoryGroupCreateRequest
): Promise<BoundaryTerritoryGroupResponse> => {
  const requestUrl = `/api/clients/${clientId}/boundary-territory-group`;

  return apiPost(requestUrl, request);
};

export const bulkUpdateBoundaryTerritoryGroups = async (
  clientId: number,
  data: readonly BoundaryTerritoryGroupUpdateRequest[],
): Promise<Readonly<{ data: readonly BoundaryTerritoryGroup[] }>> => {
  const requestUrl = `/api/clients/${clientId}/boundary-territory-group/bulk-edit`;

  const response = await apiPost<{readonly data: readonly BoundaryTerritoryGroupResponse[]}>(requestUrl, { data });
  return {
    data: response.data.map(createTerritoryBoundaryGroupFromResponse),
  };
};

const updateSingleBoundaryTerritoryGroup = (
  clientId: number,
  request: BoundaryTerritoryGroupUpdateRequest
): Promise<BoundaryTerritoryGroup> => {
  const requestUrl = `/api/clients/${clientId}/boundary-territory-group/${request.boundary_territory_group_id}`;

  const headers: Record<string, string> = {};

  if (request.etag?.timestamp) {
    headers['If-Match'] = request.etag.timestamp.toString();
  }

  return apiPut<BoundaryTerritoryGroupResponse>(requestUrl, request, { headers })
    .then(response => createTerritoryBoundaryGroupFromResponse(response));
};

const USE_BULK_UPDATE_FOR_SINGLE_UPDATE = true;
export const updateBoundaryTerritoryGroup = async (
  clientId: number,
  request: BoundaryTerritoryGroupUpdateRequest
): Promise<BoundaryTerritoryGroup> => {
  if (!USE_BULK_UPDATE_FOR_SINGLE_UPDATE) {
    return updateSingleBoundaryTerritoryGroup(clientId, request);
  }

  const { data } = await bulkUpdateBoundaryTerritoryGroups(clientId, [request]);

  if (notEmpty(data)) {
    return data[0];
  }

  console.error(`INVARIANT: Somehow, btg update request returned no response. Request: ${JSON.stringify(request, null, 2)}. Response: ${JSON.stringify(data)}`);
  throw new Error(`INVARIANT: Somehow, btg update request returned no response. Request: ${JSON.stringify(request, null, 2)}. Response: ${JSON.stringify(data)}`);
};

export const removeBoundaryTerritoryGroup = (clientId: number, boundaryTerritoryGroupsId: number) => {
  const requestUrl = `/api/clients/${clientId}/boundary-territory-group/${boundaryTerritoryGroupsId}`;

  return apiDelete(requestUrl);
};

export type UpdateBoundaryTerritoryAssignmentsRequest = {
  boundary_territory_id: string;
  column_id?: string;
  set?: {
    boundary_id: number;
  }[];
  remove?: number[];
};

export const updateBoundaryTerritoryAssignments = (clientId: number, boundaryTerritoryGroupId: number, requests: ReadonlyArray<UpdateBoundaryTerritoryAssignmentsRequest>) => {
  const requestUrl = `/api/clients/${clientId}/boundary-territory-group/${boundaryTerritoryGroupId}/boundary-territory-assignment`;
  const requestParams = {
    params: requests,
  };

  return apiPut(requestUrl, requestParams);
};
